<template>
  <div class="container py-5">

    <div class="buttons pb-3">
      <router-link
        :to="{ name: 'OrderMaker' }"
        class="button is-primary">
        Add an Order
      </router-link>
      <a class="button"
        v-if="selected.length > 0"
        @click="launchConfirmDelete(true)">
        <span class="icon is-medium">
          <i class="fas fa-trash has-text-danger"></i>
        </span>
        <span>Delete {{ selected.length }} Selected Orders</span>
      </a>
    </div>

    <article 
      v-if="!loading && orders.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no orders.
      </div>
    </article>

    <table 
      v-if="loading || orders.length > 0" 
      class="table is-fullwidth is-hoverable is-clickable has-small-font">

      <thead>
        <tr>
          <th>
            <v-checkbox 
              :value="allSelected" 
              @input="toggleAllSelected($event)"/>
          </th>
          <th 
            v-for="(header, headerIndex) in headers"
            :key="headerIndex">
            {{ header }}
          </th>
          <th></th>
        </tr>
        <tr v-if="loading">
          <th :colspan="headers.length + 2" class="py-0 px-0 is-naked">
            <v-progress class="is-fullwidth" v-model="loading"/>
          </th>
        </tr>
      </thead>

      <v-fade-transition mode="out-in">
        <tbody v-if="orders.length > 0">
          <tr
            class="v-centered"
            v-for="order in orders"
            :key="order.id"
            @click="viewOrder(order)">

            <td>
              <v-checkbox 
                @click.native.stop=""
                :value="selected.indexOf(order.id) != -1"
                @input="toggleSelected(order.id)"/>
            </td>
            <td>
              {{ order.order_no }}
            </td>
            <td>
              {{ order.desc }}
            </td>
            <td>
              <span v-if="order.supplier">{{ order.supplier.name }}</span>
            </td>
            <td>
              {{ order.price | currency }}
            </td>
            <td>
              {{ order.total_price | currency }}
            </td>
            <td>
              {{ order.currency }}
            </td>
            <td>
              <v-styled-checkbox 
                :value="order.is_ordered"
                :disabled="order.is_ordered"
                @click.native.stop=""
                @input="launchOrderDateModal($event, order)"/>
            </td>
            <td>
              <v-styled-checkbox 
                :value="order.is_delivered"
                @click.native.stop=""
                :disabled="order.is_delivered"
                @input="launchDeliveryDateModal($event, order)"/>
            </td>
            <td>
              <span 
                class="has-text-grey text--nowrap"
                v-if="order.ordered_on">
                {{ format(parse(order.ordered_on)) }}
              </span>
              <span v-else class="has-text-grey-lighter">No order date</span>
            </td>
            <td>
              <span
                class="has-text-grey text--nowrap" 
                v-if="order.ex_delivery_date">
                {{ format(parse(order.ex_delivery_date)) }}
              </span>
              <span v-else class="has-text-grey-lighter">No exp. delivery</span>
            </td>
            <td>
              <span 
                class="has-text-grey text--nowrap"
                v-if="order.delivered_on">
                {{ format(parse(order.delivered_on)) }}
              </span>
              <span v-else class="has-text-grey-lighter">No delivery date</span>
            </td>
            <td>
              <span 
                class="has-text-grey text--nowrap"
                v-if="order.fup_reminder_date">
                {{ format(parse(order.fup_reminder_date)) }}
              </span>
              <a 
                v-else
                class="text--nowrap"
                @click.stop="launchFollowUpDateModal(order)">
                <span class="icon">
                  <i class="fas fa-bell"></i>
                </span>
                <span>Set follow-up</span>
              </a>
            </td>

            <td>
              <div class="buttons buttons-right">
                <button 
                  @click.stop="deleteId = order.id; launchConfirmDelete()"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </button>
              </div>
            </td>

          </tr>
        </tbody>
      </v-fade-transition>

    </table>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmed()"
      @cancel="deleteId = null; confirmDelete.state = false"/>

    <add-order-date-modal
      v-model="addingOrderDate"
      @set-dates="update">
    </add-order-date-modal>
    
    <v-datepicker-modal
      v-model="deliveryDatepicker.status"
      :date="deliveryDatepicker.date"
      :title="deliveryDatepicker.title"
      @change="update({ delivered_on: $event.toISOString() })"/>

    <v-datepicker-modal
      v-model="followUpDatepicker.status"
      :date="followUpDatepicker.date"
      :title="followUpDatepicker.title"
      @change="update({ fup_reminder_date: $event.toISOString() })"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VStyledCheckbox from '@/components/base/VStyledCheckbox'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VDatepickerModal from '@/components/base/VDatepickerModal'
import VProgress from '@/components/base/VProgress'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import AddOrderDateModal from '@/components/inventory/AddOrderDateModal'

import { mapState, mapActions, mapMutations } from 'vuex'
import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VCheckbox,
    VStyledCheckbox,
    VConfirmModal, 
    VPagination,
    VDatepickerModal,
    VProgress,
    VFadeTransition,
    AddOrderDateModal
  },
  data() {
    return {
      headers: [
        'Order Number', 'Description', 'Supplier', 'Price', 'Total Cost',
        'Currency', 'Ordered', 'Delivered', 'Ordered On',
        'Expected Delivery', 'Delivered On', 'Follow-up Date'
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this order?',
        type: 'danger'
      },
      deleteId: null,
      bulk: false,
      show: false,
      addingOrderDate: false,
      modifyingOrderId: null,
      deliveryDatepicker: {
        status: null,
        date: null,
        title: 'Please select a delivery date'
      },
      followUpDatepicker: {
        status: null,
        date: null,
        title: 'Please select a follow-up date'
      },
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd') : null
      }
    }
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapState("orders", [
      "orders", "pagination", "selected", "loading", "allSelected"
    ])
  },
  watch: {
    'pagination.page'() {
      this.getOrders(this.pagination);
    }
  },
  methods: {
    ...mapActions("orders", [
      "getOrders", "updateOrder", "deleteOrder", "toggleAllSelected"
    ]),
    ...mapMutations("orders", ["changePage", "toggleSelected"]),
    initialize() {
      this.getOrders();
    },
    launchConfirmDelete(bulk=false) {
      this.bulk = bulk;
      if (bulk) {
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} Orders?`;
      } else {
        this.confirmDelete.message = 'Are you sure you want to delete this Order?'
      }
      this.confirmDelete.state = true;
    },
    deleteConfirmed() {
      this.confirmDelete.state = false;

      if (this.bulk == true) {
        this.deleteSelected();
      } else {
        this.deleteOrder(this.deleteId).then(
          () => this.deleteId = null
        );
      }
    },
    deleteSelected() {
      this.selected.forEach(orderId => {
        this.deleteOrder(orderId);
      })
    }, 
    launchOrderDateModal(v, order) {
      if (v) {
        this.modifyingOrderId = order.id;
        this.addingOrderDate = true;
      }
    },
    launchDeliveryDateModal(v, order) {
      if (v) {
        this.modifyingOrderId = order.id;
        this.deliveryDatepicker.status = true;
      }
    },
    launchFollowUpDateModal(order) {
      this.modifyingOrderId = order.id;
      this.followUpDatepicker.status = true;
    },
    update(data) {
      this.updateOrder({ 
        orderId: this.modifyingOrderId, 
        data: data
      });
    },
    viewOrder(order) {
      this.$router.push({
        name: 'OrderViewer',
        params: {
          orderId: order.id,
          order: order
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>